import { useState, useLayoutEffect } from 'react';
import { Box, Chip, Collapse } from '@mui/material';

import Geopoint from 'src/icons/Geopoint';
import ChevroneUpAndDows from 'src/icons/ChevroneUpAndDows';
import ChevroneUp from 'src/icons/ChevroneUp';
import Cross from 'src/icons/Cross';
import useSearchParams from 'src/hooks/useSearchParams';
import { openBottomSheet } from 'src/components/BottomSheet';

import SearchCityWidget from '../SearchCity';

type FilterItem = {
    id: number;
    name: string;
    selected: boolean;
};

type CollectionsFilterProps = {
    cities: string[];
    filters?: Array<FilterItem>;
    isDisabled?: boolean;
};

export default function CollectionsFilter({ cities = [], filters = [], isDisabled }: CollectionsFilterProps) {
    const {
        params: { city, location },
        setParams,
    } = useSearchParams();

    const [open, setIsOpen] = useState(false);
    const [selectedCities, setSelectedCities] = useState<string[]>([]);

    const hasCityVariants = isDisabled ? false : cities.length > 0;

    const hasOnlyOneCity = cities.length === 1;

    // Инициализация при первом рендере
    useLayoutEffect(() => {
        if (location) {
            const citiesFromUrl = location.includes(',') ? location.split(',') : [location];
            setSelectedCities(citiesFromUrl);
        } else if (city) {
            setSelectedCities([city]);
        }
    }, []); // Только при монтировании

    const handleOpenCityList = hasCityVariants
        ? () => {
              openBottomSheet(
                  <SearchCityWidget
                      cities={cities}
                      onSubmit={onCitiesSelectHandler}
                      activeCity={location || city}
                  />
              );
          }
        : undefined;

    const handleOpenAccordion = () => {
        setIsOpen(true);
    };

    const handleFilterClick = (_id: number) => () => {
        // Handle filter click
    };

    const onCitiesSelectHandler = (cities: string[]) => {
        setSelectedCities(cities);
        const locationParam = cities.length > 0 ? cities.join(',') : '';
        setParams({ location: locationParam });
    };

    const handleClearSelection = () => {
        setSelectedCities([]);
        setParams({ location: '' });
    };

    const renderCollapseButton = () => {
        if (filters.length < 4) return null;

        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={32}
                height={32}
                border="1px solid #E3ECF3"
                borderRadius="50%"
                sx={{
                    cursor: 'pointer',
                }}
                onClick={handleOpenAccordion}
            >
                <Box
                    style={{
                        transform: open ? 'none' : 'rotate(180deg)',
                        transformOrigin: 'center',
                    }}
                >
                    <ChevroneUp />
                </Box>
            </Box>
        );
    };

    if (cities.length < 1) {
        return null;
    }

    return (
        <Collapse
            in={open}
            collapsedSize={'fit-content'}
        >
            <Box
                display="flex"
                gap={1}
            >
                <Chip
                    label={
                        selectedCities.length === 0
                            ? 'Все локации'
                            : selectedCities.length === 1
                            ? selectedCities[0]
                            : `Локации (${selectedCities.length})`
                    }
                    clickable={!hasOnlyOneCity}
                    variant="outlined"
                    deleteIcon={
                        selectedCities.length > 0 ? (
                            <Box
                                width={16}
                                height={16}
                                borderRadius={'50%'}
                                bgcolor="background.default"
                                p={0.5}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearSelection();
                                }}
                                sx={{ cursor: 'pointer' }}
                            >
                                <Cross />
                            </Box>
                        ) : (
                            <Box>
                                <ChevroneUpAndDows />
                            </Box>
                        )
                    }
                    sx={{
                        background: '#fff',
                    }}
                    onClick={handleOpenCityList}
                    onDelete={selectedCities.length > 0 ? handleClearSelection : handleOpenCityList}
                    icon={
                        <Box
                            pl={1}
                            mr={-1}
                        >
                            <Geopoint />
                        </Box>
                    }
                />

                {filters.map((filter) => {
                    const addProps = {} as any;

                    if (filter.selected) {
                        addProps.deleteIcon = (
                            <Box
                                width={16}
                                height={16}
                                borderRadius={'50%'}
                                bgcolor="background.default"
                                p={0.5}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Cross />
                            </Box>
                        );
                        addProps.onDelete = () => {};
                    }

                    return (
                        <Chip
                            key={filter.id}
                            label={filter.name}
                            variant={filter.selected ? 'filled' : 'outlined'}
                            color={filter.selected ? 'primary' : 'default'}
                            onClick={handleFilterClick(filter.id)}
                            clickable
                            {...addProps}
                        />
                    );
                })}
                {renderCollapseButton()}
            </Box>
        </Collapse>
    );
}
